import { useCallback } from "react";
import useWebSocket from "react-use-websocket";
import useIdleTimeout from "./useIdleTimeout";
import SettingsStore from "@/stores/SettingsStore";
import { bbLog, bbWarn, previewText } from "@/lib/blockbrainTrace";
import { ChatStore } from "@/stores/ChatStore";

import type {
  ExtensionMiddleTierToolResponse,
  InputAudioBufferAppendCommand,
  InputAudioBufferClearCommand,
  Message,
  ResponseAudioDelta,
  ResponseAudioTranscriptDelta,
  ResponseDone,
  ResponseInputAudioTranscriptionCompleted,
  SessionUpdateCommand,
  SessionCreated,
  InputAudioBufferSpeechStopped,
  InputAudioBufferCommitted,
  ConversationItemCreated,
  ResponseCreated,
  ResponseOutputItemAdded,
  ResponseContentPartAdded,
  ResponseContentPartDone,
  ResponseTextDelta,
  ResponseTextDone,
  ResponseAudioDone,
  ResponseFunctionCallArgumentsDelta,
  ResponseOutputItemDone,
  RateLimitsUpdated,
  ErrorEvent,
} from "@/types";

type Parameters = {
  // useDirectAoaiApi?: boolean; // If true, the middle tier will be skipped and the AOAI ws API will be called directly
  // aoaiEndpointOverride?: string;
  // aoaiApiKeyOverride?: string;
  // aoaiModelOverride?: string;
  enableInputAudioTranscription?: boolean;
  useKnowledgeBase?: boolean;
  useSchadenKI?: boolean;
  /**
   * When true, force tool_choice="auto" and always send the system_message,
   * regardless of useKnowledgeBase. Used by the Blockbrain hybrid mode so the
   * ask_blockbrain tool stays available even while SchadenKI is active.
   */
  forceToolsAvailable?: boolean;
  systemMessage?: string;
  onWebSocketOpen?: () => void;
  onWebSocketClose?: () => void;
  onWebSocketError?: (event: Event) => void;
  onWebSocketMessage?: (event: MessageEvent<string>) => void;
  /** Called when the realtime session is idle for too long (default 5 minutes). */
  onIdleTimeout?: () => void;

  // Enhanced session configuration options
  voice?: "alloy" | "ash" | "ballad" | "coral" | "echo" | "sage" | "shimmer" | "verse";
  inputAudioFormat?: "pcm16" | "g711_ulaw" | "g711_alaw";
  outputAudioFormat?: "pcm16" | "g711_ulaw" | "g711_alaw";
  transcriptionModel?: "whisper-1" | "gpt-4o-transcribe";
  temperature?: number;
  maxResponseTokens?: number | "inf";

  onReceivedResponseAudioDelta?: (message: ResponseAudioDelta) => void;
  onReceivedExtensionMiddleTierToolResponse?: (message: ExtensionMiddleTierToolResponse) => void;
  onReceivedInputAudioBufferSpeechStarted?: (message: Message) => void;
  onReceivedResponseDone?: (message: ResponseDone) => void;
  onReceivedResponseAudioTranscriptDelta?: (message: ResponseAudioTranscriptDelta) => void;
  onReceivedInputAudioTranscriptionCompleted?: (message: ResponseInputAudioTranscriptionCompleted) => void;
  onReceivedError?: (message: Message) => void;

  // New event handlers for missing events
  onReceivedSessionCreated?: (message: SessionCreated) => void;
  onReceivedInputAudioBufferSpeechStopped?: (message: InputAudioBufferSpeechStopped) => void;
  onReceivedInputAudioBufferCommitted?: (message: InputAudioBufferCommitted) => void;
  onReceivedConversationItemCreated?: (message: ConversationItemCreated) => void;
  onReceivedResponseCreated?: (message: ResponseCreated) => void;
  onReceivedResponseOutputItemAdded?: (message: ResponseOutputItemAdded) => void;
  onReceivedResponseContentPartAdded?: (message: ResponseContentPartAdded) => void;
  onReceivedResponseContentPartDone?: (message: ResponseContentPartDone) => void;
  onReceivedResponseTextDelta?: (message: ResponseTextDelta) => void;
  onReceivedResponseTextDone?: (message: ResponseTextDone) => void;
  onReceivedResponseAudioDone?: (message: ResponseAudioDone) => void;
  onReceivedResponseFunctionCallArgumentsDelta?: (message: ResponseFunctionCallArgumentsDelta) => void;
  onReceivedResponseOutputItemDone?: (message: ResponseOutputItemDone) => void;
  onReceivedRateLimitsUpdated?: (message: RateLimitsUpdated) => void;
  onReceivedErrorEvent?: (message: ErrorEvent) => void;
};

export default function useRealTime({
  // useDirectAoaiApi,
  // aoaiEndpointOverride,
  // aoaiApiKeyOverride,
  // aoaiModelOverride,
  enableInputAudioTranscription,
  useKnowledgeBase,
  useSchadenKI,
  forceToolsAvailable,
  systemMessage,
  voice,
  inputAudioFormat,
  outputAudioFormat,
  transcriptionModel = "whisper-1",
  temperature,
  maxResponseTokens,
  onWebSocketOpen,
  onWebSocketClose,
  onWebSocketError,
  onWebSocketMessage,
  onIdleTimeout,
  onReceivedResponseDone,
  onReceivedResponseAudioDelta,
  onReceivedResponseAudioTranscriptDelta,
  onReceivedInputAudioBufferSpeechStarted,
  onReceivedExtensionMiddleTierToolResponse,
  onReceivedInputAudioTranscriptionCompleted,
  onReceivedError,
  // New event handlers
  onReceivedSessionCreated,
  onReceivedInputAudioBufferSpeechStopped,
  onReceivedInputAudioBufferCommitted,
  onReceivedConversationItemCreated,
  onReceivedResponseCreated,
  onReceivedResponseOutputItemAdded,
  onReceivedResponseContentPartAdded,
  onReceivedResponseContentPartDone,
  onReceivedResponseTextDelta,
  onReceivedResponseTextDone,
  onReceivedResponseAudioDone,
  onReceivedResponseFunctionCallArgumentsDelta,
  onReceivedResponseOutputItemDone,
  onReceivedRateLimitsUpdated,
  onReceivedErrorEvent,
}: Parameters) {
  // Optional override pulled from the Admin-managed Realtime config in Cosmos.
  // When set to a non-empty wss://-URL the WebSocket connects directly to that
  // absolute, cross-origin endpoint (used for the ALZ/APIM-Bypass via a separate
  // public-ingress Container App). Otherwise we keep the original same-origin
  // path "/realtime", which works both for the Vite dev-proxy and any setup
  // where the WS-Upgrade can traverse the HTTP ingress.
  const realtimeWebSocketUrlOverride = SettingsStore(state => state.realtimeWebSocketUrl);
  const wsEndpoint = realtimeWebSocketUrlOverride && realtimeWebSocketUrlOverride.length > 0
    ? realtimeWebSocketUrlOverride
    : "/realtime";

  const { sendJsonMessage } = useWebSocket(wsEndpoint, {
    onOpen: () => onWebSocketOpen?.(),
    onClose: () => onWebSocketClose?.(),
    onError: event => onWebSocketError?.(event),
    onMessage: event => onMessageReceived(event),
    shouldReconnect: () => true,
    reconnectAttempts: 5,
    reconnectInterval: (attemptNumber: number) => Math.min(1000 * Math.pow(2, attemptNumber), 30000),
  });

  const startSession = useCallback(() => {
    const command: SessionUpdateCommand = {
      type: "session.update",
      session: {
        modalities: ["text", "audio"], // Enable both text and audio modalities
      },
    };

    // Configure audio settings
    if (voice) {
      command.session.voice = voice;
    }
    if (inputAudioFormat) {
      command.session.input_audio_format = inputAudioFormat;
    }
    if (outputAudioFormat) {
      command.session.output_audio_format = outputAudioFormat;
    }

    // Configure transcription
    if (enableInputAudioTranscription) {
      command.session.input_audio_transcription = {
        model: transcriptionModel,
      };
    }

    // Configure response parameters
    if (temperature !== undefined) {
      command.session.temperature = temperature;
    }
    if (maxResponseTokens !== undefined) {
      command.session.max_response_output_tokens = maxResponseTokens;
    }

    // Configure system instructions
    if (systemMessage) {
      command.session.instructions = systemMessage;
    }

    // Configure tool choice based on knowledge base / forceToolsAvailable.
    // forceToolsAvailable wins so the Blockbrain tool stays callable in any mode.
    if (forceToolsAvailable) {
      command.session.tool_choice = "auto";
    } else if (useKnowledgeBase !== undefined) {
      command.session.tool_choice = useKnowledgeBase ? "auto" : "none";
    }

    // Configure turn detection with optimized settings
    command.session.turn_detection = {
      type: "server_vad",
      threshold: 0.5,
      prefix_padding_ms: 300,
      silence_duration_ms: 600,
      create_response: !useSchadenKI, // Disable auto-responses when SchadenKI is active
    };

    bbLog("realtime.sessionUpdate", {
      forceToolsAvailable: !!forceToolsAvailable,
      useSchadenKI: !!useSchadenKI,
      useKnowledgeBase: !!useKnowledgeBase,
      tool_choice: command.session.tool_choice,
      create_response: command.session.turn_detection.create_response,
      instructionsLen: (command.session.instructions || "").length,
      hasAskBlockbrainHint: (command.session.instructions || "").includes("ask_blockbrain"),
    });

    sendJsonMessage(command);
  }, [
    enableInputAudioTranscription,
    useKnowledgeBase,
    useSchadenKI,
    forceToolsAvailable,
    systemMessage,
    voice,
    inputAudioFormat,
    outputAudioFormat,
    transcriptionModel,
    temperature,
    maxResponseTokens,
    sendJsonMessage
  ]);

  const endSession = () => {
    sendJsonMessage({ type: "session.end" });
  };

  const idleTimeout = 5 * 60 * 1000;
  const { resetIdleTimer } = useIdleTimeout({
    callback: () => {
      // Same sequence as a natural idle expiry: close WS session, then notify UI.
      endSession();
      onIdleTimeout?.();
    },
    idleTimeout,
  });

  const addUserAudio = useCallback((base64Audio: string) => {
    const command: InputAudioBufferAppendCommand = {
      type: "input_audio_buffer.append",
      audio: base64Audio,
    };

    sendJsonMessage(command);
  }, [sendJsonMessage]);

  const inputAudioBufferClear = useCallback(() => {
    const command: InputAudioBufferClearCommand = {
      type: "input_audio_buffer.clear",
    };

    sendJsonMessage(command);
  }, [sendJsonMessage]);

  const onMessageReceived = (event: MessageEvent<string>) => {
    resetIdleTimer();

    onWebSocketMessage?.(event);

    let message: Message;
    try {
      message = JSON.parse(event.data);
    } catch (e) {
      console.error("Failed to parse JSON message:", e);
      throw e;
    }

    switch (message.type) {
      case "session.created":
        onReceivedSessionCreated?.(message as SessionCreated);
        break;
      case "response.done":
        if (ChatStore.getState().isWaitingForBlockbrain) {
          bbLog("realtime.responseDone.whileWaiting", {
            status: (message as ResponseDone)?.response?.status,
          });
        }
        onReceivedResponseDone?.(message as ResponseDone);
        break;
      case "response.audio.delta":
        onReceivedResponseAudioDelta?.(message as ResponseAudioDelta);
        break;
      case "response.audio_transcript.delta":
        onReceivedResponseAudioTranscriptDelta?.(message as ResponseAudioTranscriptDelta);
        break;
      case "input_audio_buffer.speech_started":
        onReceivedInputAudioBufferSpeechStarted?.(message);
        break;
      case "input_audio_buffer.speech_stopped":
        onReceivedInputAudioBufferSpeechStopped?.(message as InputAudioBufferSpeechStopped);
        break;
      case "input_audio_buffer.committed":
        onReceivedInputAudioBufferCommitted?.(message as InputAudioBufferCommitted);
        break;
      case "conversation.item.input_audio_transcription.completed":
        onReceivedInputAudioTranscriptionCompleted?.(message as ResponseInputAudioTranscriptionCompleted);
        break;
      case "conversation.item.created":
        onReceivedConversationItemCreated?.(message as ConversationItemCreated);
        break;
      case "response.created":
        onReceivedResponseCreated?.(message as ResponseCreated);
        break;
      case "response.output_item.added":
        onReceivedResponseOutputItemAdded?.(message as ResponseOutputItemAdded);
        break;
      case "response.content_part.added":
        onReceivedResponseContentPartAdded?.(message as ResponseContentPartAdded);
        break;
      case "response.content_part.done":
        onReceivedResponseContentPartDone?.(message as ResponseContentPartDone);
        break;
      case "response.text.delta":
        onReceivedResponseTextDelta?.(message as ResponseTextDelta);
        break;
      case "response.text.done":
        onReceivedResponseTextDone?.(message as ResponseTextDone);
        break;
      case "response.audio.done":
        onReceivedResponseAudioDone?.(message as ResponseAudioDone);
        break;
      case "response.function_call_arguments.delta":
        onReceivedResponseFunctionCallArgumentsDelta?.(message as ResponseFunctionCallArgumentsDelta);
        break;
      case "response.output_item.done": {
        const doneMsg = message as ResponseOutputItemDone;
        const item = (doneMsg as { item?: { type?: string; name?: string; call_id?: string; arguments?: string } }).item;
        if (item?.type === "function_call") {
          bbLog("realtime.functionCall.done", {
            name: item.name,
            call_id: item.call_id,
            argsPreview: previewText(item.arguments, 160),
            waiting: ChatStore.getState().isWaitingForBlockbrain,
          });
        }
        onReceivedResponseOutputItemDone?.(doneMsg);
        break;
      }
      case "rate_limits.updated":
        onReceivedRateLimitsUpdated?.(message as RateLimitsUpdated);
        break;
      case "extension.middle_tier_tool_response":
        const toolResponse = message as ExtensionMiddleTierToolResponse;
        onReceivedExtensionMiddleTierToolResponse?.(toolResponse);
        break;
      case "error": {
        // Enhanced error handling with detailed logging
        const errorEvent = message as ErrorEvent;
        const errorInfo = errorEvent.error;

        console.error(`[Realtime API Error] ${errorInfo.type}: ${errorInfo.message}`, {
          code: errorInfo.code,
          param: errorInfo.param,
          event_id: errorEvent.event_id,
          timestamp: new Date().toISOString()
        });
        if (ChatStore.getState().isWaitingForBlockbrain) {
          bbWarn("realtime.error.whileWaiting", {
            type: errorInfo.type,
            code: errorInfo.code,
            message: errorInfo.message,
          });
        }

        // Call both the generic error handler and specific error event handler
        onReceivedError?.(message);
        onReceivedErrorEvent?.(errorEvent);
        break;
      }
    }
  };

  return {
    startSession,
    endSession,
    addUserAudio,
    inputAudioBufferClear,
    resetIdleTimer,
    // Send a user text message using Realtime "conversation.item.create"
    sendUserText: useCallback((text: string) => {
      if (!text || !text.trim()) return;
      bbLog("realtime.sendUserText", {
        preview: previewText(text),
        waiting: ChatStore.getState().isWaitingForBlockbrain,
      });
      sendJsonMessage({
        type: "conversation.item.create",
        item: {
          type: "message",
          role: "user",
          content: [
            {
              type: "input_text",
              text,
            },
          ],
        },
      });
      // The backend auto-triggers response.create on conversation.item.create
    }, [sendJsonMessage]),
    // Manually request a response for the existing conversation. Useful when
    // VAD auto-response is disabled (e.g. SchadenKI mode) but we still want a
    // tool-driven answer for an already-transcribed user utterance.
    triggerResponse: useCallback(() => {
      bbLog("realtime.triggerResponse.create", {
        waiting: ChatStore.getState().isWaitingForBlockbrain,
        sticky: ChatStore.getState().inBlockbrainConversation,
      });
      sendJsonMessage({ type: "response.create" });
    }, [sendJsonMessage]),
    // Enhanced session management
    updateSessionConfig: useCallback((updates: Partial<SessionUpdateCommand["session"]>) => {
      const command: SessionUpdateCommand = {
        type: "session.update",
        session: updates,
      };
      sendJsonMessage(command);
    }, [sendJsonMessage]),
  };
}
